import { useState } from 'react';
import { useAppStore } from '@/store';
import { buildLUT } from '@/engine/lut-builder';
import { buildWorkingDomain } from '@/engine/domain';

// =============================================================================
// Validation summary
// =============================================================================

function ValidationRow({ label, value, ok }: { label: string; value: string; ok: boolean }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className={`font-mono ${ok ? 'text-emerald-400' : 'text-red-400'}`}>
        {ok ? '✓' : '✕'} {value}
      </span>
    </div>
  );
}

// =============================================================================
// BuildView
// =============================================================================

export default function BuildView() {
  const domain      = useAppStore(s => s.session.domain);
  const patches     = useAppStore(s => s.session.patches);
  const diagnostics = useAppStore(s => s.session.diagnostics);
  const lutOpts     = useAppStore(s => s.session.lutOptions);
  const lut         = useAppStore(s => s.session.lut);
  const setLUT    = useAppStore(s => s.setLUT);
  const setOpts   = useAppStore(s => s.setLUTOptions);
  const setStatus = useAppStore(s => s.setStepStatus);
  const setView   = useAppStore(s => s.setView);

  const [building, setBuilding]   = useState(false);
  const [progress, setProgress]   = useState(0);
  const [elapsed,  setElapsed]    = useState<number | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);

  if (!domain || !patches || !diagnostics) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 font-mono text-sm">
        Set the working domain first.
      </div>
    );
  }

  const handleBuild = () => {
    setBuilding(true);
    setBuildError(null);
    setStatus('build', 'running');
    setProgress(10);

    // Run on next tick so progress renders first
    setTimeout(() => {
      setProgress(40);
      setTimeout(() => {
        try {
          // Deep-copy patches — Zustand+Immer freezes state; buildWorkingDomain mutates flags
          const patchesCopy = patches.map(p => ({ ...p, flags: { ...p.flags } }));
          buildWorkingDomain(patchesCopy, domain);
          const result = buildLUT(patchesCopy, domain, lutOpts, diagnostics.anchors);
          setProgress(100);
          setLUT(result);
          setElapsed(result.buildTimeMs);
        } catch (e) {
          setBuildError(e instanceof Error ? e.message : String(e));
          setStatus('build', 'error');
        } finally {
          setBuilding(false);
        }
      }, 0);
    }, 20);
  };

  const val = lut?.validation;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Options panel */}
      <div className="w-[320px] shrink-0 border-r border-slate-700 p-5 space-y-5 overflow-y-auto">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Build 3D LUT</h1>
          <p className="text-xs text-slate-400 mt-0.5">{patches.length} patches · domain set</p>
        </div>

        {/* Grid size */}
        <div className="space-y-1">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Grid size</p>
          {([17, 33] as const).map(gs => (
            <label key={gs} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
              <input type="radio" name="gs" value={gs}
                checked={lutOpts.gridSize === gs}
                onChange={() => setOpts({ gridSize: gs })}
                className="accent-sky-400" />
              {gs}³ ({gs === 17 ? 'fast' : 'high quality'})
            </label>
          ))}
        </div>

        {/* Smoothing */}
        <div className="space-y-1">
          <p className="text-xs text-slate-400 uppercase tracking-wide">
            Smoothing <span className="font-mono text-sky-300 normal-case">{lutOpts.smoothing.toFixed(2)}</span>
          </p>
          <input type="range" min={0} max={1} step={0.05} value={lutOpts.smoothing}
            onChange={e => setOpts({ smoothing: Number(e.target.value) })}
            className="w-full accent-sky-400" />
        </div>

        {/* Interpolation */}
        <div className="space-y-1">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Interpolation</p>
          {(['tetrahedral','trilinear'] as const).map(m => (
            <label key={m} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
              <input type="radio" name="interp" value={m}
                checked={lutOpts.interpolation === m}
                onChange={() => setOpts({ interpolation: m })}
                className="accent-sky-400" />
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </label>
          ))}
        </div>

        <button onClick={handleBuild} disabled={building}
          className="w-full py-2 rounded-lg bg-sky-500 hover:bg-sky-400
                     disabled:opacity-50 disabled:cursor-not-allowed
                     text-slate-900 font-semibold text-sm transition-colors">
          {building ? '⟳ Building…' : '▶ Build DeviceLink'}
        </button>

        {lut && (
          <button onClick={() => setView('export')}
            className="w-full py-2 rounded-lg border border-emerald-600
                       text-emerald-400 text-sm font-medium hover:bg-emerald-900/30 transition-colors">
            → Export
          </button>
        )}
      </div>

      {/* Result panel */}
      <div className="flex-1 p-5 overflow-y-auto space-y-5">
        {buildError && (
          <div className="rounded-lg border border-red-700 bg-red-900/20 px-4 py-3">
            <p className="text-red-400 font-medium text-sm">Build failed</p>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">{buildError}</p>
          </div>
        )}
        {building && (
          <div className="space-y-3">
            <p className="text-sm text-sky-400 font-mono animate-pulse">Building 3D LUT…</p>
            <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
              <div className="h-full bg-sky-500 transition-all duration-200 rounded-full"
                style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {lut && !building && (
          <>
            <div className="rounded-lg border border-emerald-700 bg-emerald-900/20 px-4 py-3">
              <p className="text-emerald-400 font-medium text-sm">
                ✓ LUT built in {(elapsed ?? lut.buildTimeMs).toFixed(0)} ms
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {lut.gridSize}³ grid · {lut.options.interpolation}
              </p>
            </div>

            {val && (
              <div className="rounded-lg border border-slate-700 bg-slate-800/50 px-4 py-3 space-y-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Validation</p>
                <ValidationRow label="Monotonic"  value={`${val.nonMonotonicCount} violations`} ok={val.monotonic} />
                <ValidationRow label="Max ΔE"    value={val.maxError.toFixed(2)}  ok={val.maxError < 1} />
                <ValidationRow label="Mean ΔE"   value={val.meanError.toFixed(2)} ok={val.meanError < 0.5} />
              </div>
            )}

          </>
        )}

        {!lut && !building && (
          <div className="flex items-center justify-center h-48 text-slate-600 font-mono text-sm">
            Press ▶ Build DeviceLink to start
          </div>
        )}
      </div>
    </div>
  );
}
